import {
    RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import DragManager from './DragManager';
import InertiaManager from './InertiaManager';
import {
    CLICK_MILLISECONDS_THRESHOLD,
    CLICK_PIXEL_THRESHOLD,
} from './constants';
import Coordinates from './types/Coordinates';

const noop = () => {};

type GrabOptions = {
    dragStartRef: RefObject<HTMLElement>;
    movingElementRef: RefObject<HTMLElement>;
    disabled?: boolean;
    enableInertia?: boolean;
    onDragStart?: (event: PointerEvent) => void;
    onDragUpdate?: (coordinates: Coordinates) => void;
    onDragEnd?: (event: PointerEvent) => void;
    defaultPosition?: Coordinates;
    clickThreshold?: number;
    clickTimeThreshold?: number;
};

const useGrab = ({
    dragStartRef,
    movingElementRef,
    disabled,
    enableInertia = false,
    onDragStart,
    onDragUpdate,
    onDragEnd,
    defaultPosition,
}: GrabOptions) => {
    const [isGrabbing, setIsGrabbing] = useState(false);
    const pointerDownTime = useRef<number>(0);
    const originalTarget = useRef<EventTarget | null>(null);
    const dragStartElement = useRef<HTMLElement>();
    const scrollObserver = useRef<ResizeObserver | null>(null);
    const initialPointerPosition = useRef<Coordinates>({ x: 0, y: 0 });

    // Create persistent references to managers
    const dragManagerRef = useRef(new DragManager(onDragStart));
    const inertiaManagerRef = useRef<InertiaManager | null>(null);

    const movingElement = movingElementRef.current;

    const notifyScrollUpdate = useCallback(() => {
        if (!movingElement || !onDragUpdate) {
            return;
        }

        onDragUpdate({
            x: movingElement.scrollLeft,
            y: movingElement.scrollTop,
        });
    }, [movingElement, onDragUpdate]);

    // Initialize inertia manager if not already created
    if (!inertiaManagerRef.current && movingElement) {
        inertiaManagerRef.current = new InertiaManager((x, y) => {
            if (!movingElement) return;
            movingElement.scrollLeft += x;
            movingElement.scrollTop += y;
        }, notifyScrollUpdate);
    }

    const startGrab = useCallback(
        (event: PointerEvent) => {
            if (!movingElement) {
                return;
            }

            inertiaManagerRef.current?.stop();

            dragStartElement.current = event.currentTarget as HTMLElement;
            dragManagerRef.current.initializeDrag(event, {
                x: movingElement.scrollLeft,
                y: movingElement.scrollTop,
            });

            setIsGrabbing(true);
            originalTarget.current = event.target;
            pointerDownTime.current = Date.now();
            initialPointerPosition.current = {
                x: event.clientX,
                y: event.clientY,
            };

            if (enableInertia) {
                inertiaManagerRef.current?.reset();
            }
        },
        [enableInertia, movingElement]
    );

    const handlePointerMove = useCallback(
        (event: PointerEvent) => {
            if (!isGrabbing || !movingElement) return;

            if (!dragManagerRef.current.isCurrentlyDragging) {
                const hasExceededThreshold =
                    dragManagerRef.current.checkDragThreshold(
                        event,
                        CLICK_PIXEL_THRESHOLD,
                        CLICK_MILLISECONDS_THRESHOLD
                    );

                if (hasExceededThreshold && dragStartElement.current) {
                    dragStartElement.current.setPointerCapture(event.pointerId);
                }
            }

            if (enableInertia) {
                inertiaManagerRef.current?.updateVelocity(event);
            }

            const newPosition =
                dragManagerRef.current.calculateScrollPosition(event);
            movingElement.scrollLeft = newPosition.x;
            movingElement.scrollTop = newPosition.y;
            notifyScrollUpdate();
        },
        [enableInertia, isGrabbing, movingElement, notifyScrollUpdate]
    );

    const handlePointerUp = useCallback(
        (event: PointerEvent) => {
            setIsGrabbing(false);

            if (dragManagerRef.current.isCurrentlyDragging) {
                if (enableInertia) {
                    inertiaManagerRef.current?.startInertia();
                }
                onDragEnd?.(event);
                dragManagerRef.current.reset();
            }

            dragStartElement.current?.releasePointerCapture(event.pointerId);
        },
        [enableInertia, onDragEnd]
    );

    // Handle default position
    useLayoutEffect(() => {
        if (!movingElement || !defaultPosition || defaultPosition.x === 0) {
            return;
        }

        movingElement.scrollLeft = defaultPosition.x;
        movingElement.scrollTop = defaultPosition.y;
        notifyScrollUpdate();
    }, [defaultPosition, movingElement, notifyScrollUpdate]);

    // Setup drag start listener
    useEffect(() => {
        if (disabled || !dragStartRef.current) {
            return noop;
        }

        const el = dragStartRef.current;
        el.addEventListener('pointerdown', startGrab);
        el.style.cursor = isGrabbing ? 'grabbing' : 'grab';

        return () => {
            el.removeEventListener('pointerdown', startGrab);
        };
    }, [dragStartRef, disabled, isGrabbing, startGrab]);

    // Setup scroll event listener for manual scrolling
    useEffect(() => {
        if (!movingElement || !onDragUpdate) {
            return noop;
        }

        const handleScroll = () => {
            notifyScrollUpdate();
        };

        movingElement.addEventListener('scroll', handleScroll);

        return () => {
            movingElement.removeEventListener('scroll', handleScroll);
        };
    }, [notifyScrollUpdate, movingElement, onDragUpdate]);

    // Setup pointer move and up listeners while grabbing
    useEffect(() => {
        if (!movingElement || !isGrabbing) {
            return noop;
        }

        movingElement.addEventListener('pointerup', handlePointerUp);
        movingElement.addEventListener('pointermove', handlePointerMove);

        return () => {
            movingElement.removeEventListener('pointerup', handlePointerUp);
            movingElement.removeEventListener('pointermove', handlePointerMove);
        };
    }, [handlePointerMove, handlePointerUp, movingElement, isGrabbing]);

    // Setup resize observer
    useEffect(() => {
        if (!movingElement) {
            return noop;
        }

        scrollObserver.current = new ResizeObserver(() => {
            notifyScrollUpdate();
        });

        scrollObserver.current.observe(movingElement);

        return () => {
            scrollObserver.current?.disconnect();
        };
    }, [notifyScrollUpdate, movingElement]);

    // Cleanup inertia on unmount
    useEffect(() => {
        return () => {
            inertiaManagerRef.current?.stop();
        };
    }, []);

    return { isGrabbing };
};

export default useGrab;
